import { useState, useEffect, useContext } from "react";
import saveIcon from "../../assets/save-icon.svg";
import editIcon from "../../assets/edit-latest-icon.svg";
import { CaseStageContext } from "../../dashboard/cases/contexts/CaseContexts";
import { PatientServices } from "../../services/PatientServices";
import { handleApiErrorResponses } from "../../utils/ApisErrorHandling";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
const GeneralFindingsForm = ({ data, caseStage, saveAll }) => {
  const { patientDetails } = useContext(CaseStageContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    temperature: null,
    pulseRate: null,
    heightCm: null,
    weightKg: null,
    bmi: null,
    bloodPressure: null,
    cyanosis: "NO",
    pallor: "NO",
    oedemaInFeet: "NO",
    malnutrition: "NO",
  });
  const [errors, setErrors] = useState({});
  const location = useLocation();

  const VALIDATION_RULES = {
    heightCm: { min: 30, max: 250, label: "Height" },
    weightKg: { min: 1, max: 300, label: "Weight" },
    pulseRate: { min: 30, max: 320, label: "Pulse Rate", allowDecimal: false },
    bmi: { min: 10, max: 80, label: "BMI" },
    temperature: {
      label: "Temperature",
      min: 0.1,
      max: 104.0,
      allowDecimal: true,
    },
  };
  const sanitizeNumericInput = (
    value,
    allowDecimal = true,
    maxDecimalPlaces = null,
  ) => {
    if (value === "") return "";
    let sanitized = allowDecimal
      ? value.replace(/[^0-9.]/g, "")
      : value.replace(/[^0-9]/g, "");

    if (allowDecimal) {
      const parts = sanitized.split(".");
      if (parts.length > 2) {
        sanitized = parts[0] + "." + parts.slice(1).join("");
      }
      // Limit decimal places
      if (maxDecimalPlaces !== null && parts.length === 2) {
        sanitized = parts[0] + "." + parts[1].slice(0, maxDecimalPlaces);
      }
    }

    return sanitized;
  };

  const validateBloodPressure = (value) => {
    if (!value || value === "") return "";

    const bpRegex = /^(\d{1,3})\/(\d{1,3})$/;
    const match = value.match(bpRegex);

    if (!match) {
      return "B/P must be in format systolic/diastolic (e.g. 120/80)";
    }

    const systolic = parseInt(match[1], 10);
    const diastolic = parseInt(match[2], 10);

    if (systolic < 30 || systolic > 320) {
      return "Systolic must be between 30 and 320";
    }
    if (diastolic < 30 || diastolic > 200) {
      return "Diastolic must be between 40 and 150";
    }
    if (diastolic >= systolic) {
      return "Systolic must be greater than diastolic";
    }

    return "";
  };
  const validateField = (field, value) => {
    const rule = VALIDATION_RULES[field];
    if (!rule) return "";
    if (value === null || value === "" || value === undefined) return "";

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return `${rule.label} must be a valid number`;
    }
    if (numValue <= 0) {
      return `${rule.label} must be greater than 0`;
    }
    if (numValue < rule.min || numValue > rule.max) {
      return `${rule.label} must be between ${rule.min} and ${rule.max}`;
    }
    return "";
  };

  const handleNumericChange =
    (field, allowDecimal = true, maxDecimalPlaces = null) =>
    (e) => {
      const sanitized = sanitizeNumericInput(
        e.target.value,
        allowDecimal,
        maxDecimalPlaces,
      );
      setFormData((prev) => ({
        ...prev,
        [field]: sanitized,
      }));
    };

  const handleBlurValidate = (field) => () => {
    const errorMsg = validateField(field, formData[field]);
    setErrors((prev) => ({
      ...prev,
      [field]: errorMsg,
    }));
  };

  useEffect(() => {
    const heightVal = parseFloat(formData.heightCm);
    const weightVal = parseFloat(formData.weightKg);

    if (
      !isNaN(heightVal) &&
      !isNaN(weightVal) &&
      heightVal > 0 &&
      weightVal > 0
    ) {
      const heightInMeters = heightVal / 100;
      const calculatedBmi = weightVal / (heightInMeters * heightInMeters);
      const roundedBmi = Math.round(calculatedBmi * 10) / 10;

      setFormData((prev) => ({
        ...prev,
        bmi: roundedBmi,
      }));

      const bmiError = validateField("bmi", roundedBmi);
      setErrors((prev) => ({
        ...prev,
        bmi: bmiError,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        bmi: null,
      }));
      setErrors((prev) => ({
        ...prev,
        bmi: "",
      }));
    }
  }, [formData.heightCm, formData.weightKg]);

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(VALIDATION_RULES).forEach((field) => {
      const errorMsg = validateField(field, formData[field]);
      newErrors[field] = errorMsg;
      if (errorMsg) isValid = false;
    });

    const bpError = validateBloodPressure(formData.bloodPressure);
    newErrors.bloodPressure = bpError;
    if (bpError) isValid = false;

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const handleUpdate = async () => {
    if (!validateAllFields()) {
      window.alert("Please fix the validation errors before saving.");
      return;
    }

    setIsSubmitting(true);
    console.log("formData from update:", formData);
    const data = {
      ...formData,
      height: formData?.heightCm,
      weight: formData?.weightKg,
      bpLtArm: formData?.bloodPressure,
      feetOedema: formData?.oedemaInFeet,
    };
    try {
      await PatientServices.updatePresentIllness(
        data,
        patientDetails?.presentillnesses[0]?.id,
      );
      toast.success("General Findings Data Saved");
      setIsEditMode(false);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    const data = patientDetails?.presentillnesses?.[0];

    if (!data) return;

    setFormData({
      temperature: data?.temperature ?? 98,
      pulseRate: data?.pulseRate ?? 91,
      heightCm: data?.height ?? 165,
      weightKg: data?.weight ?? 65,
      bmi: data?.bmi ?? null,
      bloodPressure: data?.bpLtArm ?? null,

      cyanosis: data?.cyanosis?.toLowerCase() === "yes" ? "YES" : "NO",
      pallor: data?.pallor?.toLowerCase() === "yes" ? "YES" : "NO",
      oedemaInFeet: data?.feetOedema?.toLowerCase() === "yes" ? "YES" : "NO",
      malnutrition: data?.malnutrition?.toLowerCase() === "yes" ? "YES" : "NO",
    });
    setErrors({});
  }, [patientDetails]);

  useEffect(() => {
    if (saveAll && isEditMode) {
      handleUpdate();
    }
  }, [saveAll]);
  return (
    <div className="w-full ">
      <div className="w-full h-0 border border-solid border-[#00000010] bg-[#00000010]" />{" "}
      <div className="grid grid-cols-3 2xl:grid-cols-4 gap-[6px] 2xl:gap-[4px] 3xl:gap-3 pt-[30px]">
        {/* Temperature */}
        <div className="relative">
          <input
            type="text"
            inputMode="decimal"
            value={formData.temperature ?? ""}
            disabled={!isEditMode}
            onChange={handleNumericChange("temperature", true, 2)}
            onBlur={handleBlurValidate("temperature")}
            placeholder="Temperature*"
            className={`w-full h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid pl-[32px] pr-[110px] outline-none placeholder:font-lufga placeholder:font-normal placeholder:text-[14px] placeholder:leading-[12px] placeholder:tracking-[-0.03em] ${
              errors.temperature
                ? "border-[#FF4343] bg-[#FF43434D]"
                : "border-[#D9D9D9] bg-transparent"
            }`}
          />

          {formData.temperature && (
            <span className="absolute right-[32px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
              Temperature
            </span>
          )}

          {errors.temperature && (
            <span className="absolute left-[32px] top-full mt-[4px] font-lufga font-normal text-[11px] text-[#FF4343]">
              {errors.temperature}
            </span>
          )}
        </div>

        {/* Pulse Rate */}
        <div className="relative">
          <input
            type="text"
            inputMode="numeric"
            value={formData.pulseRate ?? ""}
            disabled={!isEditMode}
            onChange={handleNumericChange("pulseRate", false)}
            onBlur={handleBlurValidate("pulseRate")}
            placeholder="Pulse Rate Per Minute*"
            className={`w-full h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid pl-[32px] pr-[110px] outline-none placeholder:font-lufga placeholder:font-normal placeholder:text-[14px] placeholder:leading-[12px] placeholder:tracking-[-0.03em] ${
              errors.pulseRate
                ? "border-[#FF4343] bg-[#FF43434D]"
                : "border-[#D9D9D9] bg-transparent"
            }`}
          />

          {formData.pulseRate && (
            <span className="absolute right-[32px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
              Pulse Rate Per Minute
            </span>
          )}
          {errors.pulseRate && (
            <span className="absolute left-[32px] top-full mt-[4px] font-lufga font-normal text-[11px] text-[#FF4343]">
              {errors.pulseRate}
            </span>
          )}
        </div>

        {/* Height */}
        <div className="relative ">
          <input
            type="text"
            inputMode="decimal"
            value={formData.heightCm ?? ""}
            disabled={!isEditMode}
            onChange={handleNumericChange("heightCm", true)}
            onBlur={handleBlurValidate("heightCm")}
            placeholder="Height in CM*"
            className={`w-full h-[90px]  xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid px-[32px] outline-none placeholder:font-lufga placeholder:font-normal placeholder:text-[14px] placeholder:leading-[12px] placeholder:tracking-[-0.03em] ${
              errors.heightCm
                ? "border-[#FF4343] bg-[#FF43434D]"
                : "border-[#D9D9D9] bg-transparent"
            }`}
          />
          {formData.heightCm && (
            <span className="absolute right-[32px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
              Height in cm
            </span>
          )}
          {errors.heightCm && (
            <span className="absolute left-[32px] top-full mt-[4px] font-lufga font-normal text-[11px] text-[#FF4343]">
              {errors.heightCm}
            </span>
          )}
        </div>

        {/* Weight */}
        <div className="relative">
          <input
            type="text"
            inputMode="decimal"
            value={formData.weightKg ?? ""}
            disabled={!isEditMode}
            onChange={handleNumericChange("weightKg", true)}
            onBlur={handleBlurValidate("weightKg")}
            placeholder="Weight in KG*"
            className={`w-full h-[90px] xl:h-[52px]  2xl:h-[52px]  3xl:h-[52px] rounded-[42px] border border-solid px-[32px]  outline-none placeholder:font-lufga placeholder:font-normal placeholder:text-[14px] placeholder:leading-[12px] placeholder:tracking-[-0.03em] placeholder:text-[#8B8B8B] ${
              errors.weightKg
                ? "border-[#FF4343] bg-[#FF43434D]"
                : "border-[#D9D9D9] bg-transparent"
            }`}
          />
          {formData.weightKg && (
            <span className="absolute right-[32px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
              Weight in kg
            </span>
          )}
          {errors.weightKg && (
            <span className="absolute left-[32px] top-full mt-[4px] font-lufga font-normal text-[11px] text-[#FF4343]">
              {errors.weightKg}
            </span>
          )}
        </div>

        {/* BMI - auto-calculated, read-only */}
        <div className="relative ">
          <input
            type="text"
            value={formData.bmi ?? ""}
            disabled
            readOnly
            placeholder="BMI"
            className={`w-full h-[90px]  xl:h-[52px]  2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid px-[32px]  outline-none placeholder:font-lufga placeholder:font-normal placeholder:text-[14px] placeholder:leading-[12px] placeholder:tracking-[-0.03em] placeholder:text-[#8B8B8B] ${
              errors.bmi
                ? "border-[#FF4343] bg-[#FF43434D]"
                : "border-[#D9D9D9] bg-transparent"
            }`}
          />
          {formData.bmi && (
            <span className="absolute right-[32px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
              BMI
            </span>
          )}
          {errors.bmi && (
            <span className="absolute left-[32px] top-full mt-[4px] font-lufga font-normal text-[11px] text-[#FF4343]">
              {errors.bmi}
            </span>
          )}
        </div>

        {/* B/P */}
        <div className="relative w-full">
          <input
            type="text"
            value={formData.bloodPressure ?? ""}
            disabled={!isEditMode}
            onChange={(e) => {
              // Only allow digits and a single slash
              const sanitized = e.target.value.replace(/[^0-9/]/g, "");
              setFormData((prev) => ({ ...prev, bloodPressure: sanitized }));
            }}
            onBlur={() => {
              const errorMsg = validateBloodPressure(formData.bloodPressure);
              setErrors((prev) => ({ ...prev, bloodPressure: errorMsg }));
            }}
            placeholder="B/P (e.g. 120/80)"
            className={`w-full h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid px-[32px] outline-none placeholder:font-lufga placeholder:font-normal placeholder:text-[14px] placeholder:leading-[12px] placeholder:tracking-[-0.03em] placeholder:text-[#8B8B8B] ${
              errors.bloodPressure
                ? "border-[#FF4343] bg-[#FF43434D]"
                : "border-[#D9D9D9] bg-transparent"
            }`}
          />
          {formData.bloodPressure && (
            <span className="absolute right-[32px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
              Blood Pressure
            </span>
          )}
          {errors.bloodPressure && (
            <span className="absolute left-[32px] top-full mt-[4px] font-lufga font-normal text-[11px] text-[#FF4343]">
              {errors.bloodPressure}
            </span>
          )}
        </div>

        {/* Cyanosis */}
        <div className="w-full h-[90px]  xl:h-[52px]  2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[14px] pr-[4px] flex items-center">
          {" "}
          <span className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] whitespace-nowrap">
            {" "}
            Cyanosis
          </span>
          <div className="ml-auto flex items-center gap-[2px]">
            {" "}
            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  cyanosis: "YES",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.cyanosis === "YES"
                  ? "bg-[#5AC69F] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              Yes
            </button>
            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  cyanosis: "NO",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.cyanosis === "NO"
                  ? "bg-[#FF4343] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              No
            </button>
          </div>
        </div>

        {/* Pallor */}
        <div className="w-full h-[90px]  xl:h-[52px]  2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[14px] pr-[4px] flex items-center">
          <span className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] whitespace-nowrap">
            Pallor
          </span>

          <div className="ml-auto flex items-center gap-[2px]">
            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  pallor: "YES",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.pallor === "YES"
                  ? "bg-[#5AC69F] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              Yes
            </button>

            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  pallor: "NO",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.pallor === "NO"
                  ? "bg-[#FF4343] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              No
            </button>
          </div>
        </div>

        {/* Oedema */}
        <div className="w-full h-[90px]  xl:h-[52px]  2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] pl-[14px] pr-[4px] flex items-center">
          {" "}
          <span className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] whitespace-nowrap">
            {" "}
            Oedema in Feet
          </span>
          <div className="ml-auto flex items-center gap-[2px]">
            {" "}
            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  oedemaInFeet: "YES",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.oedemaInFeet === "YES"
                  ? "bg-[#5AC69F] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              Yes
            </button>
            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  oedemaInFeet: "NO",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.oedemaInFeet === "NO"
                  ? "bg-[#FF4343] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              No
            </button>
          </div>
        </div>

        {/* Malnutrition */}
        <div className="w-full h-[90px]  xl:h-[52px]  2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] pl-[14px] pr-[4px] flex items-center">
          {" "}
          <span className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] whitespace-nowrap">
            {" "}
            Malnutrition
          </span>
          <div className="ml-auto flex items-center gap-[2px]">
            {" "}
            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  malnutrition: "YES",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.malnutrition === "YES"
                  ? "bg-[#5AC69F] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              Yes
            </button>
            <button
              disabled={!isEditMode}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  malnutrition: "NO",
                }))
              }
              className={`w-[66px] h-[41px] rounded-[100px] px-[20px] py-[16px] border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
                formData.malnutrition === "NO"
                  ? "bg-[#FF4343] text-white"
                  : "bg-[#F5F5F5] text-[#000000]"
              }`}
            >
              No
            </button>
          </div>
        </div>
      </div>
      {!location?.pathname?.toLowerCase()?.includes("editcases") && (
        <div>
          {isEditMode ? (
            <div className="flex justify-end mt-[12px]">
              <button
                className="w-[170px] h-[74px] xl:w-[94.97px] xl:h-[40.97px] 2xl:w-[94.97px] 2xl:h-[40.97px] border border-none rounded-full bg-black flex justify-center items-center gap-[8px] font-lufga text-[14px] text-[#68FF9F] font-normal"
                onClick={handleUpdate}
                disabled={isSubmitting}
              >
                SAVE
                <img src={saveIcon} alt="save" className="pl-[16px]" />
              </button>
            </div>
          ) : (
            <div className="flex justify-end items-center gap-[10px] mt-[12px]">
              <button className="w-[170px] h-[74px] xl:w-[101px] xl:h-[42px] 2xl:w-[101px] 2xl:h-[42px] rounded-full border border-none bg-[#5AC69F] flex justify-center items-center gap-[10px] font-lufga text-[14px] text-white font-normal">
                SAVED
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                onClick={() => setIsEditMode(true)}
                className="w-[42px] h-[42px] rounded-full border border-solid border-[#5AC69F] flex justify-center items-center bg-transparent"
              >
                <img src={editIcon} alt="edit icon" width={9} height={18} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GeneralFindingsForm;
